import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarX2, TrendingUp, Users, ArrowRight, Check, CalendarDays } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";
import { PulseMark } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import HeroVideo from "@/components/marketing/HeroVideo";

const painPoints = [
  { icon: CalendarX2, title: "Double bookings", desc: "Two clients, one farmhouse, one date. Pulse enforces a single booking per day so it never happens by accident again." },
  { icon: TrendingUp, title: "No revenue clarity", desc: "Insert an amount to every booking and watch your monthly and annual financial data analytics." },
  { icon: Users, title: "No employee visibility", desc: "Invite staff to manage bookings while your revenue stays private. Everyone sees the same live calendar." },
];

const CAL_BOOKINGS = [
  { day: 5, color: "#3B82F6", amount: 85000 },
  { day: 12, color: "#8B5CF6", amount: 60000 },
  { day: 18, color: "#3B82F6", amount: 45000 },
  { day: 22, color: "#8B5CF6", amount: 55000 },
  { day: 26, color: "#3B82F6", amount: 90000 },
];

function DashboardVisual({ animated = true }) {
  const days = Array.from({ length: 35 }, (_, i) => i - 2);
  const [step, setStep] = React.useState(CAL_BOOKINGS.length);
  React.useEffect(() => {
    if (!animated) return;
    const t = setInterval(() => setStep((s) => (s >= CAL_BOOKINGS.length ? 1 : s + 1)), 1100);
    return () => clearInterval(t);
  }, [animated]);
  const shown = CAL_BOOKINGS.slice(0, step);
  const bookedMap = Object.fromEntries(shown.map((b) => [b.day, b.color]));
  const revenue = shown.reduce((a, b) => a + b.amount, 0);
  return (
    <div className="relative rounded-xl border border-slate-200 bg-white p-4 shadow-pulse-lg sm:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-md bg-pulse-navy"><PulseMark size={18} color="#FFFFFF" /></div>
          <span className="text-sm font-semibold text-pulse-navy">June 2026</span>
        </div>
        {animated ? (
          <span className="flex items-center gap-1.5 rounded-full bg-pulse-green/10 px-2.5 py-1 text-[11px] font-semibold text-pulse-green">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pulse-green" /> Live preview
          </span>
        ) : (
          <div className="flex gap-2">
            <span className="rounded-md bg-[#3B82F6]/10 px-2.5 py-1 text-xs font-medium text-[#3B82F6]">Nirvana</span>
            <span className="rounded-md bg-[#8B5CF6]/10 px-2.5 py-1 text-xs font-medium text-[#8B5CF6]">Niyara</span>
          </div>
        )}
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1.5">
        {["S","M","T","W","T","F","S"].map((d, i) => (
          <div key={i} className="text-center text-[10px] font-medium uppercase text-slate">{d}</div>
        ))}
        {days.map((d, i) => {
          const valid = d >= 1 && d <= 30;
          const color = bookedMap[d];
          return (
            <div key={i}
                 className={`aspect-square rounded-md p-1 text-[10px] font-medium transition-all duration-500 ${valid ? "border border-slate-100" : ""} ${color ? "scale-100" : "scale-95"} flex items-start justify-start`}
                 style={color ? { background: color, color: "#fff" } : {}}>
              {valid ? d : ""}
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-md bg-cloud px-4 py-3">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-slate">Revenue this month</div>
          <div className="text-xl font-bold tabular-nums text-pulse-navy">₹{revenue.toLocaleString("en-IN")}</div>
        </div>
        <TrendingUp className="text-pulse-green" size={22} />
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const startTrial = () => navigate(user ? "/dashboard" : "/login");
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-2">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22,1,0.36,1] }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-cloud px-3.5 py-1.5 text-xs font-medium text-slate">
                <span className="h-1.5 w-1.5 rounded-full bg-pulse-green" /> Calendar product · now live
              </div>
              <h1 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-pulse-navy sm:text-5xl md:text-6xl">
                The heartbeat<br />of your business.
              </h1>
              <p className="mt-6 max-w-md text-lg text-slate">
                Pulse brings you an AI-run calendar where you can manage your bookings, revenue, expenses and analytical reports. Built for farmhouse and venue owners.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button onClick={startTrial} data-testid="hero-trial-btn"
                  className="group flex items-center gap-2 rounded-md bg-pulse-green px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-150 hover:scale-[1.02] active:scale-[0.97]">
                  Start your free trial
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </button>
                <Link to="/products" className="text-sm font-semibold text-pulse-navy underline-offset-4 hover:underline" data-testid="hero-products-link">
                  See the product →
                </Link>
              </div>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.22,1,0.36,1] }}>
            <HeroVideo />
          </motion.div>
        </div>
      </section>

      {/* Pain points */}
      <section className="bg-cloud py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-xs font-medium uppercase tracking-wider text-pulse-green">The problems we solve</div>
              <h2 className="mt-3 text-4xl font-bold tracking-tight text-pulse-navy md:text-5xl">Running a venue shouldn't feel this chaotic.</h2>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {painPoints.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="group h-full rounded-xl border border-slate-200 bg-white p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-pulse-md">
                  <div className="grid h-12 w-12 place-items-center rounded-md bg-pulse-navy/5 text-pulse-navy transition-colors group-hover:bg-pulse-green group-hover:text-white">
                    <p.icon size={22} />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-pulse-navy">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Product preview */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-pulse-green">One product, today</div>
              <h2 className="mt-3 text-4xl font-bold tracking-tight text-pulse-navy md:text-5xl">The Calendar</h2>
              <p className="mt-5 text-lg text-slate">A booking calendar per venue, revenue that adds itself up, and analytics that finally make sense.</p>
              <ul className="mt-8 space-y-3">
                {["Colour-coded calendar for every venue","Single booking per day, with an easy override","Confirmed vs tentative bookings","Revenue and expense record","Analytical reports"].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-charcoal">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-pulse-green/10 text-pulse-green"><Check size={13} /></span>
                    {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/products")} className="mt-8 flex items-center gap-2 text-sm font-semibold text-pulse-navy hover:underline underline-offset-4" data-testid="home-explore-calendar">
                Explore Calendar <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-xl bg-pulse-navy p-3 shadow-pulse-lg">
              <DashboardVisual animated={false} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pricing preview / CTA */}
      <section className="bg-cloud py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <div className="mx-auto grid w-fit place-items-center rounded-2xl bg-pulse-navy p-4"><CalendarDays className="text-pulse-green" size={28} /></div>
            <h2 className="mt-6 text-4xl font-bold tracking-tight text-pulse-navy md:text-5xl">One simple plan. Everything included.</h2>
            <p className="mx-auto mt-4 max-w-lg text-lg text-slate">Booking calendar, revenue analytics and unlimited employee accounts — at no extra cost. Try it free for 15 days.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <button onClick={startTrial} data-testid="cta-pricing-btn"
                className="rounded-md bg-pulse-green px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.97]">
                Start your free trial
              </button>
              <button onClick={() => navigate("/pricing")} className="rounded-md border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-pulse-navy transition-colors hover:bg-white/60">
                See pricing
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
